import React from 'react';
import moment from 'moment';
import { cloneDeep } from 'lodash-es';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import {
  getDeviceValuesByPageId,
  getDeviceOptionByType,
  getMetricsTablePlatformsCascaderOptions,
} from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import { PC_CONSOLE_DATABRAIN_DEFAULT_METRICS } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { createPlatformCascaderDisplayRenderConfig } from '@intelligence/_config/components/filters/platformCascaderDisplayRender';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { ENV } from '@home/_config/config';
const entityType = 'combine';

const getStartDateOfTopChartsCombineCompaniesFetcher = (): string => {
  // VGI 的数据从2025-10-01之后就没数据了，所以这里要从2025-01-01开始查询，不然公司筛选器就没有选项数据了
  return ['dev', 'test'].includes(ENV) ? '2024-01-01' : '2025-01-01';
};

const companyFilterParams = {
  entity_type: entityType,
  source: 'steamcharts',
  data_sources: ['newzoo', 'mscience', 'steamcharts', 'npd'],
  metric: 'pcu',
  region_type: 'region',
  regions: ['global'],
  granularity: 'monthly',
  start_date: '',
  end_date: '',
  page_size: 500,
  search_name: '',
  page: 1,
  source_metric: {
    steamcharts: ['pcu'],
  },
};

const platformCascaderDefaultValueNull = {
  mscience: [], // 'PC', 'PlayStation', 'Xbox', 'Nintendo'
  gsd: [], // 'PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'
  vginsights: [], // 'PC'
  npd: [], // 'PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch', 'WII U'
  ampere: [], // 'PC', 'PlayStation', 'Xbox'
  alinea: [], // 'PC', 'PlayStation', 'Xbox'
  newzoo: [], // 'PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'
};

// 筛选器value默认值
export const DEFAULT_FILTER_VALUE_COMBINE = {
  [filterKeyMap.MY_FILTER]: {
    value: '',
  },
  [filterKeyMap.RELEASE_TIME]: {
    value: ['', ''],
  },
  [filterKeyMap.ALL_COMPANYS]: { value: [] },
  [filterKeyMap.PUBLISHER_COMPANY]: { value: [] },
  [filterKeyMap.PUBLISHER_CLASS]: { value: [] },
  [filterKeyMap.PUBLISHER_REGION]: {
    type: 'market',
    regions: [],
    markets: ['global'],
  },
  [filterKeyMap.DEVELOPER]: { value: [] },
  [filterKeyMap.STEAM_TAGS]: { value: [], logic: 'AND' },
  [filterKeyMap.GAME_MODE]: { value: [] },
  [filterKeyMap.GAME_ENGINE]: { value: [], logic: 'OR' },
  [filterKeyMap.GENRETOR]: {
    source: 'iegg',
    mainGenres: [],
    subGenres: [],
  },
  [filterKeyMap.DIGITAL_SEGMENT_MULTIPLE]: {
    value: ['All'],
  },
  [filterKeyMap.ALL_Games]: {
    value: [],
    keywords: '',
    isSearchAll: true,
    checkedList: [],
  },
  [filterKeyMap.DIGITAL_RANGE]: { value: { min: '', max: '' } },
  [filterKeyMap.PCU_RANGE]: { value: { min: '', max: '' } },
  [filterKeyMap.WISHLISTS_RANGE]: { value: { min: '', max: '' } },
  [filterKeyMap.PHYSICAL_RANGE]: { value: { min: '', max: '' } },
  [filterKeyMap.PRICE_RANGE]: { value: { min: '', max: '' } },
  [filterKeyMap.PREMIUM_RANGE]: { value: { min: '', max: '' } },
  [filterKeyMap.REGION]: {
    regions: [],
    markets: ['global'],
    type: 'market',
  },
  [filterKeyMap.RANGE_TIME]: {
    type: 'week',
    value: ['', ''],
  },
  [filterKeyMap.METRICTABLE_DEVICE]: {
    value: getDeviceValuesByPageId('PCConsoleMetricsTable'),
  },
  [filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS]: {
    value: [
      'revenue###digitalRevenue',
      'revenue###units',
      'revenue###gsd_digitalUnits',
      'revenue###gsd_physicalUnits',
      'steamData###pcu',
      'steamData###acu',
      'steamData###steamfollowers',
      'steamData###steamreviews',
      'steamData###est_revenue',
      'steamData###est_units',
    ],
  },

  [filterKeyMap.PLATFORMCASCADER]: {
    value: platformCascaderDefaultValueNull,
  },
  [filterKeyMap.PUBLISHER]: {
    value: 'NETWORK',
  },
};

export const DEFAULT_FILTER_VALUE_DATABRAIN = {
  ...DEFAULT_FILTER_VALUE_COMBINE,
  [filterKeyMap.RANGE_TIME]: {
    type: 'date',
    value: ['', ''],
  },
  [filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS]: {
    value: PC_CONSOLE_DATABRAIN_DEFAULT_METRICS,
  },
};

// 筛选器自定义布局列表
export const FILTER_KEY_LIST_COMBINE = [
  filterKeyMap.MY_FILTER,
  filterKeyMap.RANGE_TIME,
  filterKeyMap.REGION,
  filterKeyMap.GENRETOR,
  filterKeyMap.HOT_TAGS_SOURCE,
  filterKeyMap.ALL_Games,
  // filterKeyMap.METRICTABLE_DEVICE,
  filterKeyMap.PLATFORMCASCADER, // 新增
  filterKeyMap.PUBLISHER_COMPANY,
  filterKeyMap.PUBLISHER_CLASS,
  filterKeyMap.PUBLISHER_REGION,
  filterKeyMap.DEVELOPER,
  filterKeyMap.STEAM_TAGS,
  filterKeyMap.GAME_MODE,
  filterKeyMap.GAME_ENGINE,
  filterKeyMap.PCU_RANGE,
  filterKeyMap.WISHLISTS_RANGE,
  filterKeyMap.PHYSICAL_RANGE,
  filterKeyMap.DIGITAL_RANGE,
  filterKeyMap.PRICE_RANGE,
  filterKeyMap.PREMIUM_RANGE,
  filterKeyMap.RELEASE_TIME,
  filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS,
];

// 筛选器list配置（原始项定义，保持每项配置不变；最终对外导出走重排后的数组）
const RAW_DEFAULT_INPUT_LIST_COMBINE = [
  {
    savePopupVisble: 0,
    key: 'myFilter',
    style: { width: 140 },
    value: {
      value: '',
    },
  },
  {
    savePopupVisble: 1,
    type: 'rangeTime',
    key: 'rangeTime',
    startOfWeek: 0,
    showSelectType: true,
    showShortcut: true,
    componentDefault: {},
  },
  {
    savePopupVisble: 1,
    key: 'region',
    default: {
      regions: [],
      markets: ['global'],
      type: 'market',
    },
    visible: 1,
    // disableNoChina 改由 specialParams 动态注入（含 Ampere 指标时启用 'Global except China mainland'）
    options: [],
    className: 'hot-tags-region-select',
    entityType: 'combine',
    showRadioBubble: true,
  },
  {
    savePopupVisble: 1,
    key: 'genretor',
    className: 'dbi-user-guide__topcharts-pc-games__filter__genre-select hot-tags-genre-select',
    default: {
      source: 'iegg',
      mainGenres: [],
      subGenres: [],
    },
    disableAnnie: true,
    disableIegg: false,
    genreData: {
      genreRef: {
        current: {},
      },
      genreSource: 'iegg',
      mainGenres: [],
      subGenres: [],
    },
    entityType: 'combine',
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.HOT_TAGS_SOURCE,
    options: [],
  },
  {
    savePopupVisble: 1,
    key: 'allGames',
    default: {
      value: [],
      keywords: '',
      isSearchAll: true,
      checkedList: [],
    },
    params: {
      entity_type: 'combine',
      game_type: 'current_game',
    },
    chooseUpNum: 1000,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.METRICTABLE_DEVICE,
    type: filterKeyMap.CONSOLE_DEVICE,
    options: getDeviceOptionByType(TopchartPageId.PCConsoleMetricsTable),
    showIcon: true,
    visible: 2,
    btnName: 'dbi_platform',
    default: getDeviceValuesByPageId(TopchartPageId.PCConsoleMetricsTable),
    AWidth: 356,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.PLATFORMCASCADER,
    options: getMetricsTablePlatformsCascaderOptions(
      { [filterKeyMap.CASCADER]: platformCascaderDefaultValueNull },
      createPlatformCascaderDisplayRenderConfig({ width: 320 }),
    ),
    type: 'cascader',
    visible: 1,
    visibleinlist: 1,
    groupNameLocaleKey: 'dbi_label_metrics',
    itemNameLocaleKey: 'dbi_label_metrics',
  },
  {
    key: filterKeyMap.PUBLISHER,
    title: 'dbi_metric_publisher',
    default: [],
    style: { width: '280px' },
    singleType: 'single',
    isShowButtonLabel: true,
    options: [
      {
        text: 'dbi_metric_publisher_All_value',
        value: 'all',
        // img: annieImg,
      },
      {
        text: 'dbi_metric_publisher_shared_value',
        value: 'NETWORK',
        // img: sensorImg,
        tip: 'dbi_metric_publisher_shared',
        tipMetrics: 'data_usage',
      },
      {
        text: 'dbi_metric_publisher_retail_value',
        value: 'RETAIL',
        // img: sensorImg,
        tip: 'dbi_metric_publisher_retail',
        tipMetrics: 'data_usage',
      },
    ],
  },
  {
    savePopupVisble: 1,
    key: 'steam_tag',
    AWidth: 440,
    ref: {
      current: {},
    },
    default: {
      value: [],
    },
    options: [],
  },
  {
    savePopupVisble: 1,
    key: 'game_mode',
    AWidth: 370,
    ref: {
      current: {},
    },
    default: {
      value: [],
    },
    selectorConfig: {
      placeholder: 'dbi_top_game_mode_placeholder',
      searchPlaceholder: 'dbi_top_game_mode_search_placeholder',
      label: 'dbi_top_game_mode',
      tipLabel: 'dbi_top_game_mode',
      tipText: 'dbi_top_game_mode_tip',
    },
    options: [],
  },
  {
    savePopupVisble: 1,
    key: 'game_engine',
    AWidth: 440,
    ref: {
      current: {},
    },
    default: {
      value: [],
      logic: 'AND',
    },
    selectorConfig: {
      placeholder: 'dbi_top_game_engine_placeholder',
      searchPlaceholder: 'dbi_top_game_engine_search_placeholder',
      label: 'dbi_top_game_engine',
      tipLabel: 'dbi_top_game_engine',
      tipText: 'dbi_top_game_engine_tip',
      logicLabel: 'dbi_game_engine_logic_label',
      logicTipText: 'dbi_game_engine_logic_tip',
    },
    options: [],
  },
  {
    unitText: '-',
    title: 'dbi_pcu_range',
    savePopupVisble: 1,
    leftPlaceholder: 'dbi_minimum',
    rightPlaceholder: 'dbi_maximum',
    key: 'pcu_range',
    default: {
      value: {
        min: '',
        max: '',
      },
    },
  },
  {
    title: 'dbi_physical_revenue_range',
    width: 360,
    savePopupVisble: 1,
    key: 'physical_range',
    default: {
      value: {
        min: '',
        max: '',
      },
    },
  },
  {
    title: 'dbi_digital_revenue_range',
    width: 370,
    savePopupVisble: 1,
    key: 'digital_range',
    default: {
      value: {
        min: '',
        max: '',
      },
    },
  },
  {
    title: 'dbi_price_range',
    savePopupVisble: 1,
    leftPlaceholder: 'dbi_minimum',
    rightPlaceholder: 'dbi_maximum',
    key: 'price_range',
    default: {
      value: {
        min: '',
        max: '',
      },
    },
  },
  {
    title: 'dbi_premium_revenue_range',
    width: 360,
    savePopupVisble: 1,
    key: 'premium_range',
    default: {
      value: {
        min: '',
        max: '',
      },
    },
  },
  {
    savePopupVisble: 1,
    type: 'rangeTime',
    key: 'releaseTime',
    calendarType: 'date',
    showSelectType: false,
    showShortcut: false,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.ALL_COMPANYS,
    ref: {
      current: {},
    },
    title: 'dbi_company',
    getFetchParams: () => ({
      ...companyFilterParams,
      start_date: getStartDateOfTopChartsCombineCompaniesFetcher(),
      end_date: moment().format('YYYY-MM-DD'),
    }),
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.PUBLISHER_COMPANY,
    type: filterKeyMap.ALL_COMPANYS,
    ref: {
      current: {},
    },
    default: {
      value: [],
    },
    title: 'dbi_publisher',
    getFetchParams: () => ({
      ...companyFilterParams,
      start_date: getStartDateOfTopChartsCombineCompaniesFetcher(),
      end_date: moment().format('YYYY-MM-DD'),
    }),
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.PUBLISHER_CLASS,
    type: filterKeyMap.STEAM_TAGS,
    title: 'dbi_publisher_class',
    AWidth: 440,
    ref: {
      current: {},
    },
    default: {
      value: [],
    },
    selectorConfig: {
      placeholder: 'dbi_top_publisher_class_placeholder',
      searchPlaceholder: 'dbi_top_publisher_class_search_placeholder',
      label: 'dbi_publisher_class',
      tipLabel: 'dbi_publisher_class',
      tipText: 'dbi_top_publisher_class_tip',
      tipTextParams: {
        gamalyticDataSourceIcon: (
          <img style={{ verticalAlign: 'middle' }} src={getDataSourceIcon('gamalytic')} alt="" />
        ),
        alineaDataSourceIcon: (
          <img style={{ verticalAlign: 'middle' }} src={getDataSourceIcon('alinea')} alt="" />
        ),
        mscienceDataSourceIcon: (
          <img style={{ verticalAlign: 'middle' }} src={getDataSourceIcon('mscience')} alt="" />
        ),
      },
    },
    options: [],
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.PUBLISHER_REGION,
    type: filterKeyMap.REGION,
    title: 'dbi_publisher_region',
    default: {
      type: 'market',
      regions: [],
      markets: ['global'],
    },
    hasInfoIcon: false,
    showRadioBubble: false,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.DEVELOPER,
    type: filterKeyMap.ALL_COMPANYS,
    ref: {
      current: {},
    },
    title: 'dbi_developer',
    getFetchParams: () => ({
      ...companyFilterParams,
      start_date: getStartDateOfTopChartsCombineCompaniesFetcher(),
      end_date: moment().format('YYYY-MM-DD'),
    }),
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS,
  },
  {
    unitText: '-',
    title: 'dbi_wishlists_range',
    savePopupVisble: 1,
    leftPlaceholder: 'dbi_minimum',
    rightPlaceholder: 'dbi_maximum',
    key: filterKeyMap.WISHLISTS_RANGE,
    type: filterKeyMap.PCU_RANGE,
    default: {
      value: {
        min: '',
        max: '',
      },
    },
  },
];

const findInputItem = (key: string) =>
  RAW_DEFAULT_INPUT_LIST_COMBINE.find((it: any) => it?.key === key);

export const PC_CONSOLE_FILTER_GROUPS: filterKeyMap.IFilterGroup[] = [
  {
    key: 'popular',
    titleKey: 'dbi_filter_group_popular',
    filterKeys: [
      'rangeTime',
      'region',
      filterKeyMap.PLATFORMCASCADER,
      'genretor',
      filterKeyMap.HOT_TAGS_SOURCE,
      'allGames',
      'releaseTime',
      filterKeyMap.METRICTABLE_DEVICE,
    ],
  },
  {
    key: 'company',
    titleKey: 'dbi_filter_group_company',
    filterKeys: [
      filterKeyMap.PUBLISHER_COMPANY,
      filterKeyMap.PUBLISHER_CLASS,
      filterKeyMap.PUBLISHER_REGION,
      filterKeyMap.DEVELOPER,
    ],
  },
  {
    key: 'mscience',
    titleKey: 'dbi_filter_group_mscience',
    iconCode: 'mscience',
    filterKeys: ['digital_range'],
  },
  {
    key: 'steam',
    titleKey: 'dbi_filter_group_steam',
    iconCode: 'steam',
    filterKeys: [
      'pcu_range',
      'premium_range',
      'steam_tag',
      'game_mode',
      filterKeyMap.WISHLISTS_RANGE,
      'price_range',
    ],
  },
  {
    key: 'gsd',
    titleKey: 'dbi_filter_group_gsd',
    iconCode: 'gsd',
    filterKeys: [filterKeyMap.PUBLISHER],
  },
  {
    key: 'npd',
    titleKey: 'dbi_filter_group_npd',
    iconCode: 'npd',
    filterKeys: ['physical_range'],
  },
  {
    key: 'igdb',
    titleKey: 'dbi_filter_group_igdb',
    iconCode: 'igdb',
    filterKeys: ['game_engine', filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS],
  },
];

export const DEFAULT_INPUT_LIST_COMBINE = [
  findInputItem('myFilter'),
  findInputItem('rangeTime'),
  findInputItem('region'),
  findInputItem(filterKeyMap.PLATFORMCASCADER),
  findInputItem('genretor'),
  findInputItem(filterKeyMap.HOT_TAGS_SOURCE),
  findInputItem('allGames'),
  findInputItem(filterKeyMap.PUBLISHER_COMPANY),
  findInputItem(filterKeyMap.PUBLISHER_CLASS),
  findInputItem(filterKeyMap.PUBLISHER_REGION),
  findInputItem(filterKeyMap.DEVELOPER),
  findInputItem('releaseTime'),
  findInputItem(filterKeyMap.METRICTABLE_DEVICE),
  findInputItem('digital_range'),
  findInputItem('pcu_range'),
  findInputItem('premium_range'),
  findInputItem('steam_tag'),
  findInputItem('game_mode'),
  findInputItem(filterKeyMap.WISHLISTS_RANGE),
  findInputItem('price_range'),
  findInputItem(filterKeyMap.PUBLISHER),
  findInputItem('physical_range'),
  findInputItem('game_engine'),
  findInputItem(filterKeyMap.PCCONSOLE_TOPCHARTS_METRICS),
].filter(Boolean) as any[];

const DEFAULT_INPUT_LIST_DATABRAIN = cloneDeep(DEFAULT_INPUT_LIST_COMBINE).map((item) => {
  if (item?.key === 'allGames') {
    return {
      ...item,
      params: {
        ...(item?.params || {}),
        b_only_fetch_databrain_metric: true,
      },
    };
  }
  return item;
});

export default {
  PCConsoleMetricsTable: {
    defaultFilterValue: DEFAULT_FILTER_VALUE_COMBINE,
    filterKeyList: FILTER_KEY_LIST_COMBINE,
    defaultInputList: DEFAULT_INPUT_LIST_COMBINE,
    filterGroups: PC_CONSOLE_FILTER_GROUPS,
  },
  PCConsoleDataBrainCalibration: {
    defaultFilterValue: DEFAULT_FILTER_VALUE_DATABRAIN,
    filterKeyList: FILTER_KEY_LIST_COMBINE,
    defaultInputList: DEFAULT_INPUT_LIST_DATABRAIN,
    filterGroups: PC_CONSOLE_FILTER_GROUPS,
  },
};
